<template>
  <div
    v-if="isOpen"
    class="overlay-backdrop"
    :class="{ '-visible': isOpen }"
    @touchend.stop="closeOverlay"
    @mousedown.left.stop="closeOverlay"
    @click.stop
  >
    <button class="overlay-close" @mousedown.stop @click="closeOverlay">
      <BaseIconSvoi icon="essential/xmark-clear" size="m" />
    </button>

    <div v-if="images.length > 1" class="navigation" @mousedown.stop @touchend.stop>
      <button class="nav-button" :disabled="currentIndex === 0" @click="previousImage">
        <BaseIconSvoi icon="arrow/arrow-left-2" size="m" />
      </button>
      <button class="nav-button" :disabled="currentIndex === images.length - 1" @click="nextImage">
        <BaseIconSvoi icon="arrow/arrow-right-3" size="m" />
      </button>
    </div>

    <div class="zoom-controls" @mousedown.stop @touchend.stop>
      <button class="zoom-btn" title="Увеличить" @click="zoomIn">
        <BaseIconSvoi icon="essential/add" size="s" />
      </button>
      <button class="zoom-btn" title="Уменьшить" @click="zoomOut">
        <BaseIconSvoi icon="essential/minus" size="s" />
      </button>
    </div>

    <div ref="overlayContent" class="overlay-content">
      <div
        ref="imageContainer"
        :style="imageContainerStyles"
        class="image-container"
        @wheel="handleWheel"
        @mousedown.left.stop="startDrag"
        @touchstart.stop="handleTouchStart"
        @touchend.stop="handleTouchEnd"
      >
        <img
          :src="currentImage"
          :alt="`Изображение ${currentIndex + 1} из ${images.length}`"
          class="overlay-image"
        />
      </div>
    </div>

    <div v-if="images.length > 1" class="indicator" @mousedown.stop @touchend.stop>
      {{ currentIndex + 1 }} / {{ images.length }}
    </div>

    <div v-if="images.length > 1" class="thumbnails" @mousedown.stop @touchend.stop>
      <div
        v-for="(image, index) in images"
        :key="index"
        class="thumbnail"
        :class="{ '-active': index === currentIndex }"
        @click="goToImage(index)"
      >
        <img :src="image" :alt="`Миниатюра ${index + 1}`" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'

  interface Props {
    modelValue?: boolean
    images: string[]
    initialIndex?: number
    // Шаг зума. 10% по умолчанию
    zoomStep?: number
    // Максимальный масштаб. 300% по умолчанию
    maxScale?: number
    // Минимальный масштаб. 10% по умолчанию
    minScale?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    initialIndex: 0,
    zoomStep: 0.1,
    maxScale: 3,
    minScale: 0.1,
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
  }>()

  const overlayContent = ref<HTMLElement | null>(null)
  const imageContainer = ref<HTMLElement | null>(null)

  const currentIndex = ref(props.initialIndex)
  const scale = ref(1)
  const isDragging = ref(false)
  const position = ref({ x: 0, y: 0 })
  const dragStart = ref({ x: 0, y: 0 })
  const touchStartX = ref(0)

  const isOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
  })

  const currentImage = computed(() => props.images[currentIndex.value] || '')

  const imageContainerStyles = computed(() => ({
    transform: `translate(${position.value.x}px, ${position.value.y}px) scale(${scale.value})`,
    transition: isDragging.value ? 'none' : 'transform 0.2s ease',
    cursor: scale.value <= 1 ? 'default' : isDragging.value ? 'grabbing' : 'grab',
  }))

  const resetZoom = () => {
    scale.value = 1
    position.value = { x: 0, y: 0 }
  }

  const keepImageInView = () => {
    if (!overlayContent.value || !imageContainer.value || scale.value <= 1) {
      position.value = { x: 0, y: 0 }
      return
    }

    const containerRect = overlayContent.value.getBoundingClientRect()
    const imageRect = imageContainer.value.getBoundingClientRect()

    const maxX = Math.max(0, (imageRect.width - containerRect.width) / 2)
    const maxY = Math.max(0, (imageRect.height - containerRect.height) / 2)

    position.value = {
      x: Math.min(Math.max(position.value.x, -maxX), maxX),
      y: Math.min(Math.max(position.value.y, -maxY), maxY),
    }
  }

  const zoomIn = () => {
    scale.value = Math.min(scale.value + props.zoomStep, props.maxScale)
    keepImageInView()
  }

  const zoomOut = () => {
    scale.value = Math.max(scale.value - props.zoomStep, props.minScale)
    keepImageInView()
  }

  const previousImage = () => {
    if (currentIndex.value > 0) {
      currentIndex.value--
      resetZoom()
    }
  }

  const nextImage = () => {
    if (currentIndex.value < props.images.length - 1) {
      currentIndex.value++
      resetZoom()
    }
  }

  const goToImage = (index: number) => {
    currentIndex.value = index
    resetZoom()
  }

  const closeOverlay = () => {
    isOpen.value = false
    isDragging.value = false
    resetZoom()
  }

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault()

    if (event.deltaY < 0) {
      zoomIn()
    } else {
      zoomOut()
    }
  }

  const startDrag = (event: MouseEvent) => {
    event.preventDefault()

    if (scale.value <= 1) {
      return
    }

    isDragging.value = true
    dragStart.value = {
      x: event.clientX - position.value.x,
      y: event.clientY - position.value.y,
    }
  }

  const onDrag = (event: MouseEvent) => {
    event.preventDefault()

    if (!isDragging.value) {
      return
    }

    position.value = {
      x: event.clientX - dragStart.value.x,
      y: event.clientY - dragStart.value.y,
    }
    keepImageInView()
  }

  const stopDrag = (event: MouseEvent) => {
    event.preventDefault()
    isDragging.value = false
  }

  const handleTouchStart = (event: TouchEvent) => {
    const touch = event.changedTouches[0]
    if (touch) touchStartX.value = touch.screenX
  }

  const handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0]
    if (!touch) return

    const diff = touchStartX.value - touch.screenX
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextImage()
      } else {
        previousImage()
      }
    }
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (!props.modelValue) {
      return
    }

    if (event.key === 'Escape') {
      closeOverlay()
    } else if (event.key === 'ArrowLeft') {
      previousImage()
    } else if (event.key === 'ArrowRight') {
      nextImage()
    }
  }

  const toggleOverflow = (value: boolean) => {
    if (import.meta.client) {
      document.body.style.overflow = value ? 'hidden' : ''
    }
  }

  watch(
    () => props.modelValue,
    val => {
      toggleOverflow(val)
      if (val) {
        currentIndex.value = props.initialIndex
        resetZoom()
      }
    }
  )

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('mousemove', onDrag)
    document.addEventListener('mouseup', stopDrag)
    document.addEventListener('mouseleave', stopDrag)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.removeEventListener('mouseleave', stopDrag)
    toggleOverflow(false)
  })
</script>

<style lang="scss" scoped>
  .overlay-backdrop {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(0 0 0 / 80%);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.3s ease,
      visibility $trans-default;

    &.-visible {
      opacity: 1;
      visibility: visible;
    }

    > .overlay-close {
      position: absolute;
      top: 75px;
      right: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: rgb(255 255 255 / 10%);
      color: $color-default-white;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      transition: background-color $trans-default;
      z-index: 1002;

      &:hover {
        background: rgb(255 255 255 / 20%);
      }
    }

    > .navigation {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      padding: 0 16px;
      pointer-events: none;
      z-index: 1001;

      @include vp-767 {
        display: none;
      }

      > .nav-button {
        pointer-events: all;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background: rgb(255 255 255 / 10%);
        border: none;
        border-radius: 50%;
        color: $color-default-white;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
          background: rgb(255 255 255 / 20%);
          transform: scale(1.05);
        }

        &:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
      }
    }

    > .zoom-controls {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 10px;
      background: rgb(0 0 0 / 50%);
      padding: 10px;
      border-radius: 8px;
      z-index: 1001;

      > .zoom-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: rgb(255 255 255 / 10%);
        color: $color-default-white;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        transition: background-color $trans-default;

        &:hover {
          background: rgb(255 255 255 / 20%);
        }
      }
    }

    > .overlay-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      max-width: 90vw;
      max-height: 90vh;
      padding: 20px;
      overflow: hidden;

      > .image-container {
        display: flex;
        align-items: center;
        justify-content: center;
        height: fit-content;
        max-width: 100%;
        user-select: none;
        transform-origin: center center;
        will-change: transform;

        > .overlay-image {
          max-width: 100%;
          max-height: 70vh;
          height: auto;
          width: auto;
          object-fit: contain;

          @include vp-767 {
            max-height: 60vh;
          }
        }
      }
    }

    > .indicator {
      position: absolute;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgb(0 0 0 / 70%);
      color: $color-default-white;
      padding: 4px 12px;
      border-radius: 16px;
      font-size: 14px;
      font-weight: 500;
      z-index: 1001;
    }

    > .thumbnails {
      position: absolute;
      bottom: 80px;
      right: 20px;
      display: flex;
      gap: 8px;
      z-index: 1001;

      @include vp-767 {
        display: none;
      }

      > .thumbnail {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.2s ease;
        flex-shrink: 0;

        &.-active {
          border-color: $color-kiwi-50;
          transform: scale(1.05);
        }

        &:hover {
          border-color: $color-potato-50;
        }

        > img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }
</style>
